import React, { useState, useEffect, useCallback } from 'react';
import { Mail, Save, RotateCcw, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import apiService from '../../services/api';
import './notification-templates.scss';

interface Template {
  key: string;
  label: string;
  audiencia: string;
  disparador: string;
  subject: string;
  body: string;
  cta: string;
  default: { subject: string; body: string; cta: string };
  is_custom: boolean;
}

const NotificationTemplates: React.FC = () => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [drafts, setDrafts] = useState<Record<string, { subject: string; body: string; cta: string }>>({});
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<Record<string, { type: 'ok' | 'err'; text: string }>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.notificationTemplates.getAll();
      const data: Template[] = res.data?.data || [];
      setTemplates(data);
      const d: Record<string, any> = {};
      data.forEach((t) => { d[t.key] = { subject: t.subject, body: t.body, cta: t.cta }; });
      setDrafts(d);
    } catch (e) {
      setFeedback({ _global: { type: 'err', text: 'No se pudieron cargar las plantillas.' } });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setField = (key: string, field: 'subject' | 'body' | 'cta', value: string) => {
    setDrafts((prev) => ({ ...prev, [key]: { ...prev[key], [field]: value } }));
  };

  const isDirty = (t: Template) => {
    const d = drafts[t.key];
    return d && (d.subject !== t.subject || d.body !== t.body || d.cta !== t.cta);
  };

  const save = async (t: Template) => {
    const d = drafts[t.key];
    if (!d.subject.trim() || !d.body.trim()) {
      setFeedback((p) => ({ ...p, [t.key]: { type: 'err', text: 'Asunto y mensaje son obligatorios.' } }));
      return;
    }
    setSavingKey(t.key);
    setFeedback((p) => ({ ...p, [t.key]: undefined as any }));
    try {
      await apiService.notificationTemplates.update(t.key, d);
      setTemplates((prev) => prev.map((x) => x.key === t.key ? { ...x, ...d, is_custom: true } : x));
      setFeedback((p) => ({ ...p, [t.key]: { type: 'ok', text: 'Guardado' } }));
    } catch (e) {
      setFeedback((p) => ({ ...p, [t.key]: { type: 'err', text: 'Error al guardar.' } }));
    } finally {
      setSavingKey(null);
    }
  };

  const resetToDefault = async (t: Template) => {
    if (!window.confirm(`¿Restaurar el texto original de "${t.label}"?`)) return;
    setSavingKey(t.key);
    try {
      await apiService.notificationTemplates.reset(t.key);
      const def = t.default;
      setTemplates((prev) => prev.map((x) => x.key === t.key ? { ...x, ...def, is_custom: false } : x));
      setDrafts((prev) => ({ ...prev, [t.key]: { ...def } }));
      setFeedback((p) => ({ ...p, [t.key]: { type: 'ok', text: 'Restaurado al texto por defecto' } }));
    } catch (e) {
      setFeedback((p) => ({ ...p, [t.key]: { type: 'err', text: 'Error al restaurar.' } }));
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <div className="notif-tpl">
      <div className="notif-tpl__head">
        <div className="notif-tpl__eyebrow"><Mail size={14} /> NOTIFICACIONES DEL PROGRAMA</div>
        <h2 className="notif-tpl__title">Mensajes que <em>se envían</em></h2>
        <p className="notif-tpl__meta">
          Estos son los correos automáticos del programa. Haz clic en cada uno para ver y editar el texto que reciben las personas.
        </p>
      </div>

      {loading ? (
        <div className="notif-tpl__loading"><Loader2 className="spin" size={20} /> Cargando plantillas…</div>
      ) : feedback._global ? (
        <div className="notif-tpl__error">{feedback._global.text}</div>
      ) : (
        <div className="notif-tpl__list">
          {templates.map((t) => {
            const isOpen = !!open[t.key];
            const d = drafts[t.key] || { subject: '', body: '', cta: '' };
            const fb = feedback[t.key];
            return (
              <div className={`ntpl-card ${isOpen ? 'ntpl-card--open' : ''}`} key={t.key}>
                <button className="ntpl-card__header" onClick={() => setOpen((p) => ({ ...p, [t.key]: !p[t.key] }))}>
                  <div className="ntpl-card__headmain">
                    <span className="ntpl-card__label">{t.label}</span>
                    <span className="ntpl-card__subject">{d.subject}</span>
                  </div>
                  <div className="ntpl-card__headmeta">
                    {t.is_custom && <span className="ntpl-badge">Personalizado</span>}
                    <span className="ntpl-card__aud">{t.audiencia}</span>
                    {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </div>
                </button>

                {isOpen && (
                  <div className="ntpl-card__body">
                    <p className="ntpl-card__trigger"><strong>Cuándo se envía:</strong> {t.disparador}</p>

                    <label className="ntpl-field">
                      <span>Asunto</span>
                      <input type="text" value={d.subject} onChange={(e) => setField(t.key, 'subject', e.target.value)} />
                    </label>

                    <label className="ntpl-field">
                      <span>Mensaje</span>
                      <textarea rows={4} value={d.body} onChange={(e) => setField(t.key, 'body', e.target.value)} />
                    </label>

                    <label className="ntpl-field">
                      <span>Texto del botón (CTA)</span>
                      <input type="text" value={d.cta} onChange={(e) => setField(t.key, 'cta', e.target.value)} />
                    </label>

                    <div className="ntpl-card__actions">
                      <div className="ntpl-card__fb">
                        {fb && <span className={fb.type === 'ok' ? 'fb-ok' : 'fb-err'}>{fb.text}</span>}
                      </div>
                      {t.is_custom && (
                        <button className="ntpl-btn ntpl-btn--ghost" onClick={() => resetToDefault(t)} disabled={savingKey === t.key}>
                          <RotateCcw size={15} /> Restaurar original
                        </button>
                      )}
                      <button className="ntpl-btn ntpl-btn--primary" onClick={() => save(t)} disabled={savingKey === t.key || !isDirty(t)}>
                        {savingKey === t.key ? <Loader2 size={15} className="spin" /> : <Save size={15} />} Guardar
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default NotificationTemplates;
